import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Async Thunks
export const fetchLibrariesByChitalishteId = createAsyncThunk(
  'libraries/fetchByChitalishteId',
  async (chitalishteId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/chitalishta/${chitalishteId}/libraries`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLibraryInformationCards = createAsyncThunk(
  'libraries/fetchInformationCards',
  async (libraryId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/libraries/${libraryId}/information-cards`);
      return { libraryId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchLibraries = createAsyncThunk(
  'libraries/search',
  async (searchParams, { rejectWithValue }) => {
    try {
      const { page = 0, size = 12, sortBy = 'name', sortDirection = 'asc', ...filters } = searchParams;
      
      const params = {
        page,
        size,
        sortBy,
        sortDirection,
        ...filters
      };

      const response = await axios.get(`${API_URL}/libraries/search`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLibraryById = createAsyncThunk(
  'libraries/fetchById',
  async (libraryId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/libraries/${libraryId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const librarySlice = createSlice({
  name: 'libraries',
  initialState: {
    libraries: [],
    currentLibrary: null,
    libraryInformationCards: {},
    loading: false,
    error: null,
    pagination: {
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      size: 12
    },
    filters: {
      name: '',
      region: '',
      municipality: '',
      town: ''
    },
    sort: {
      sortBy: 'name',
      sortDirection: 'asc'
    }
  },
  reducers: {
    clearCurrentLibrary: (state) => {
      state.currentLibrary = null;
    },
    clearLibraryError: (state) => {
      state.error = null;
    },
    setLibraryFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLibrarySort: (state, action) => {
      state.sort = { ...state.sort, ...action.payload };
    },
    setLibraryPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearLibraryData: (state) => {
      state.libraries = [];
      state.currentLibrary = null;
      state.libraryInformationCards = {};
      state.pagination = {
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
        size: 12
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch libraries by chitalishte ID
      .addCase(fetchLibrariesByChitalishteId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLibrariesByChitalishteId.fulfilled, (state, action) => {
        state.loading = false;
        state.libraries = action.payload;
      })
      .addCase(fetchLibrariesByChitalishteId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Fetch library information cards
      .addCase(fetchLibraryInformationCards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLibraryInformationCards.fulfilled, (state, action) => {
        state.loading = false;
        state.libraryInformationCards[action.payload.libraryId] = action.payload.data;
      })
      .addCase(fetchLibraryInformationCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Search libraries
      .addCase(searchLibraries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchLibraries.fulfilled, (state, action) => {
        state.loading = false;
        state.libraries = action.payload.content || action.payload;
        state.pagination = {
          currentPage: action.payload.number || 0,
          totalPages: action.payload.totalPages || 1,
          totalItems: action.payload.totalElements || action.payload.length || 0,
          size: action.payload.size || 12
        };
      })
      .addCase(searchLibraries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Fetch library by ID
      .addCase(fetchLibraryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLibraryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLibrary = action.payload;
      })
      .addCase(fetchLibraryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  }
});

export const {
  clearCurrentLibrary,
  clearLibraryError,
  setLibraryFilters,
  setLibrarySort,
  setLibraryPagination,
  clearLibraryData
} = librarySlice.actions;

// Selectors
export const selectLibraries = (state) => state.libraries.libraries;
export const selectCurrentLibrary = (state) => state.libraries.currentLibrary;
export const selectLibraryInformationCards = (state, libraryId) => 
  state.libraries.libraryInformationCards[libraryId] || [];
export const selectLibraryLoading = (state) => state.libraries.loading;
export const selectLibraryError = (state) => state.libraries.error;
export const selectLibraryPagination = (state) => state.libraries.pagination;
export const selectLibraryFilters = (state) => state.libraries.filters;
export const selectLibrarySort = (state) => state.libraries.sort;

export default librarySlice.reducer;